import { useAgentDependencies } from '../hooks/useAgentActions';

interface DependencyMapProps {
  appId: string | null;
  appName?: string | null;
  onSelectApp?: (appId: string) => void;
}

export default function DependencyMap({ appId, appName, onSelectApp }: DependencyMapProps) {
  const deps = useAgentDependencies(appId);
  const data = deps.data;

  if (!appId) {
    return (
      <div className="text-sm text-gray-500 py-12 text-center">
        Select an app to view its dependency map.
      </div>
    );
  }

  if (deps.isLoading) {
    return <div className="text-sm text-gray-500 py-8 text-center">Loading dependency map…</div>;
  }

  if (deps.isError || !data) {
    return <div className="text-sm text-red-600 py-8 text-center">Failed to load dependencies.</div>;
  }

  const tools = data.tools_called.slice(0, 3);
  const agents = data.triggered_agents.slice(0, 1);
  const templates = data.templates_used.slice(0, 1);
  const coUsers = data.co_users.slice(0, 3);
  const label = appName || data.app.app_name;

  const toolYs = [80, 140, 200];
  const agentY = 260;
  const templateY = 320;
  const coUserYs = [80, 260, 320];

  return (
    <div>
      <div className="mb-4">
        <p className="text-sm text-gray-500 m-0">
          Showing dependencies for{' '}
          <strong className="text-gray-900 dark:text-gray-100 font-medium">{label}</strong>
        </p>
      </div>

      <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 overflow-hidden">
        <svg viewBox="0 0 620 380" xmlns="http://www.w3.org/2000/svg" className="w-full h-auto block" role="img">
          <title>{label} relationship map</title>

          {tools.map((_, i) => (
            <line key={`t-${i}`} x1="180" y1="190" x2="290" y2={toolYs[i]} stroke="#888780" strokeWidth="1" opacity="0.4" />
          ))}
          {agents.length > 0 && (
            <line x1="180" y1="190" x2="290" y2={agentY} stroke="#888780" strokeWidth="1" opacity="0.4" />
          )}
          {templates.length > 0 && (
            <line x1="180" y1="190" x2="290" y2={templateY} stroke="#888780" strokeWidth="1" opacity="0.4" />
          )}
          {coUsers.map((_, i) => (
            <line key={`c-${i}`} x1="430" y1={coUserYs[i]} x2="540" y2={coUserYs[i]} stroke="#888780" strokeWidth="1" opacity="0.4" />
          ))}

          <rect x="40" y="160" width="140" height="60" rx="8" fill="#E6F1FB" stroke="#185FA5" strokeWidth="1" />
          <text x="110" y="184" textAnchor="middle" fontFamily="sans-serif" fontSize="13" fontWeight="500" fill="#0C447C">
            {label.length > 16 ? `${label.slice(0, 14)}…` : label}
          </text>
          <text x="110" y="202" textAnchor="middle" fontFamily="sans-serif" fontSize="11" fill="#185FA5">
            {data.app.app_id}
          </text>

          <text x="290" y="50" fontFamily="sans-serif" fontSize="11" fontWeight="500" fill="#888">TOOLS CALLED</text>
          {tools.map((t, i) => (
            <g key={t.tool}>
              <rect x="290" y={toolYs[i] - 20} width="140" height="40" rx="6" fill="#EEEDFE" stroke="#534AB7" strokeWidth="0.5" />
              <text x="300" y={toolYs[i] - 2} fontFamily="sans-serif" fontSize="11" fontWeight="500" fill="#3C3489">
                {t.tool.length > 18 ? `${t.tool.slice(0, 16)}…` : t.tool}
              </text>
              <text x="300" y={toolYs[i] + 12} fontFamily="sans-serif" fontSize="11" fill="#534AB7">
                {t.count} calls
              </text>
              <line x1="430" y1={toolYs[i]} x2="540" y2={coUserYs[0]} stroke="#888780" strokeWidth="1" opacity="0.4" />
            </g>
          ))}

          {agents.length > 0 && (
            <>
              <text x="290" y="250" fontFamily="sans-serif" fontSize="11" fontWeight="500" fill="#888">TRIGGERED</text>
              <rect x="290" y="240" width="140" height="40" rx="6" fill="#E1F5EE" stroke="#0F6E56" strokeWidth="0.5" />
              <text x="300" y="258" fontFamily="sans-serif" fontSize="11" fontWeight="500" fill="#085041">
                {agents[0].agent}
              </text>
              <text x="300" y="272" fontFamily="sans-serif" fontSize="11" fill="#0F6E56">
                {agents[0].runs} runs · {agents[0].status}
              </text>
            </>
          )}

          {templates.length > 0 && (
            <>
              <text x="290" y="310" fontFamily="sans-serif" fontSize="11" fontWeight="500" fill="#888">USES TEMPLATES</text>
              <rect x="290" y="300" width="140" height="40" rx="6" fill="#FAECE7" stroke="#993C1D" strokeWidth="0.5" />
              <text x="300" y="318" fontFamily="sans-serif" fontSize="11" fontWeight="500" fill="#712B13">
                {templates[0].template_id.split(':').slice(-2).join(':').slice(0, 20)}
              </text>
              <text x="300" y="332" fontFamily="sans-serif" fontSize="11" fill="#993C1D">
                {templates[0].category || 'template'}
              </text>
            </>
          )}

          {coUsers.map((c, i) => (
            <g
              key={c.app_id}
              className="cursor-pointer"
              onClick={() => onSelectApp?.(c.app_id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onSelectApp?.(c.app_id)}
            >
              <rect x="540" y={coUserYs[i] - 20} width="60" height="40" rx="6" fill="#F1EFE8" stroke="#888780" strokeWidth="0.5" />
              <text x="570" y={coUserYs[i] - 2} textAnchor="middle" fontFamily="sans-serif" fontSize="11" fontWeight="500" fill="#444441">
                {c.app_name.length > 8 ? `${c.app_name.slice(0, 6)}…` : c.app_name}
              </text>
              <text x="570" y={coUserYs[i] + 12} textAnchor="middle" fontFamily="sans-serif" fontSize="11" fill="#5F5E5A">
                also
              </text>
            </g>
          ))}
        </svg>
      </div>

      <p className="text-xs text-gray-500 mt-3 mb-0 text-center">Click any node to see what calls it</p>
    </div>
  );
}
